import { useEffect, useState } from "react";
import { vpAxios, money } from "../api";
import { toast } from "sonner";
import { Clock } from "lucide-react";

/**
 * Cronômetro em tempo real (mm:ss) que corre de minuto em minuto até o pedido
 * ser marcado como entregue/pronto (o card some da tela nesse momento).
 *
 * Estados por faixa de tempo:
 *   < 10 min  → cinza (dentro do prazo)
 *   10-20 min → amarelo (atenção)
 *   ≥ 20 min  → vermelho pulsante (atrasado)
 */
function OrderTimer({ startIso }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 30000); // atualiza a cada 30s (barato)
    return () => clearInterval(iv);
  }, []);
  let start = 0;
  try { start = new Date(startIso).getTime() || 0; } catch { start = 0; }
  if (!start) return null;
  const totalSec = Math.max(0, Math.floor((now - start) / 1000));
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  const label = `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;

  let cls = "bg-neutral-100 text-neutral-700";
  let anim = "";
  if (mm >= 20) {
    cls = "bg-red-100 text-red-700 border border-red-300";
    anim = "animate-pulse";
  } else if (mm >= 10) {
    cls = "bg-amber-100 text-amber-800 border border-amber-300";
  } else {
    cls = "bg-emerald-50 text-emerald-700 border border-emerald-200";
  }
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-black px-2 py-0.5 rounded-full ${cls} ${anim}`}
      title="Tempo desde a criação do pedido"
      data-testid="vaipede-cozinha-timer"
    >
      <Clock className="w-3 h-3"/> {label}
    </span>
  );
}

export default function CozinhaPage() {
  const [rows, setRows] = useState([]);
  const load = () => vpAxios.get("/vaipede/pedidos?status=preparando,pronto,aceito").then(r => setRows(r.data));
  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  const marcar = async (id, status) => {
    try { await vpAxios.patch(`/vaipede/pedidos/${id}/status`, { status }); toast.success("Atualizado"); load(); }
    catch { toast.error("Erro"); }
  };

  return (
    <div data-testid="vaipede-cozinha">
      <h1 className="text-2xl font-black">Cozinha</h1>
      <p className="text-sm text-neutral-500 mb-6">Pedidos em produção</p>

      {rows.length === 0 && <p className="text-sm text-neutral-500">Nenhum pedido em preparo.</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map(p => (
          <div key={p.id} className="bg-white rounded-2xl border border-neutral-200 p-4">
            <div className="flex items-center justify-between mb-2 gap-2">
              <div className="font-black">#{p.numero}</div>
              <div className="flex items-center gap-2">
                <OrderTimer startIso={p.created_at}/>
                <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === "pronto" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{p.status}</span>
              </div>
            </div>
            <div className="text-xs text-neutral-500 mb-2">{p.tipo}{p.mesa_numero ? ` · Mesa ${p.mesa_numero}` : ""}{p.cliente_nome ? ` · ${p.cliente_nome}` : ""}</div>
            <div className="space-y-1 text-sm mb-3">
              {(p.itens || []).map((it, i) => <div key={i}><b>{it.quantidade}x</b> {it.nome}</div>)}
            </div>
            {p.observacao && <div className="text-xs bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-2">📝 {p.observacao}</div>}
            <div className="flex items-center justify-between text-sm mb-3"><b>Total</b><b className="text-[#EA1D2C]">{money(p.total)}</b></div>
            {p.status !== "pronto" && <button onClick={() => marcar(p.id, "pronto")} className="w-full py-2 rounded-xl bg-emerald-600 text-white font-bold text-sm" data-testid={`vaipede-pronto-${p.id}`}>Marcar pronto</button>}
            {p.status === "pronto" && p.tipo === "delivery" && (
              <div className="w-full py-2 rounded-xl bg-blue-50 border border-blue-200 text-blue-700 font-bold text-sm text-center">
                🛵 Aguardando entregador
              </div>
            )}
            {p.status === "pronto" && p.tipo !== "delivery" && (
              <button onClick={() => marcar(p.id, "entregue")} className="w-full py-2 rounded-xl bg-neutral-900 text-white font-bold text-sm" data-testid={`vaipede-entregue-${p.id}`}>Entregue ao cliente</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
